import React, { useEffect, useState } from "react";
import { User, ShieldCheck, EnvelopeSimple, DeviceMobile, GoogleLogo, LockKey, CheckCircle } from "@phosphor-icons/react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

export default function AccountSettings() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [security, setSecurity] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.get("/account/profile").then((r) => setProfile(r.data)),
    api.get("/account/security").then((r) => setSecurity(r.data)),
  ]);
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/account/profile", {
        first_name: profile.first_name, last_name: profile.last_name,
        address: profile.address, profile_picture: profile.profile_picture,
      });
      toast.success("Profile saved."); refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!profile) return <PageLoader />;

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8">
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="overline mb-1">Account</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Settings</h1>
      <Tabs defaultValue="profile">
        <TabsList className="mb-6"><TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger><TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger></TabsList>

        <TabsContent value="profile" className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>First name</Label><Input value={profile.first_name || ""} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="mt-1.5" data-testid="pf-first" /></div>
            <div><Label>Last name</Label><Input value={profile.last_name || ""} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="mt-1.5" data-testid="pf-last" /></div>
          </div>
          <div><Label>Address</Label><Input value={profile.address?.line || ""} onChange={(e) => setProfile({ ...profile, address: { ...(profile.address || {}), line: e.target.value } })} placeholder="City, area" className="mt-1.5" /></div>
          <Button onClick={saveProfile} disabled={saving} className="rounded-full" data-testid="save-profile">{saving ? "Saving…" : "Save changes"}</Button>
        </TabsContent>

        <TabsContent value="security">
          {!security ? <PageLoader /> : (
            <div className="space-y-4">
              <SecurityRow icon={EnvelopeSimple} label="Email" value={security.email} verified={security.email_verified}
                action={<ChangeContact field="email" onDone={load} />} />
              <SecurityRow icon={DeviceMobile} label="Mobile" value={security.mobile || "Not added"} verified={security.mobile_verified}
                action={<ChangeContact field="mobile" onDone={load} />} />
              <SecurityRow icon={GoogleLogo} label="Google" value={security.google_linked ? "Connected" : "Not connected"} verified={security.google_linked} />
              <SecurityRow icon={LockKey} label="Password" value={security.has_password ? "Set" : "Not set (Google login)"} verified={security.has_password}
                action={security.has_password ? <ChangePassword /> : null} />
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="overline mb-3">Recent security activity</div>
                <div className="space-y-2 text-sm">
                  {(security.recent_activity || []).slice(0, 6).map((a) => (
                    <div key={a.id} className="flex justify-between text-muted-foreground">
                      <span className="capitalize">{a.action.replace(/[._]/g, " ")}</span>
                      <span>{new Date(a.created_at).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  {(!security.recent_activity || security.recent_activity.length === 0) && <span className="text-muted-foreground text-sm">No recent activity.</span>}
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SecurityRow({ icon: Icon, label, value, verified, action }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 grid place-items-center rounded-lg bg-muted"><Icon size={18} /></span>
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-sm text-muted-foreground">{value}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {verified && <span className="text-emerald-600 inline-flex items-center gap-1 text-xs"><CheckCircle weight="fill" size={16} /> Verified</span>}
        {action}
      </div>
    </div>
  );
}

function ChangeContact({ field, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [value, setValue] = useState("");
  const [otp, setOtp] = useState("");

  const request = async () => {
    try { const { data } = await api.post("/auth/change-contact/request", { field, value }); setStep(2); toast.success(data.message); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const verify = async () => {
    try { await api.post("/auth/change-contact/verify", { field, otp }); toast.success(`${field} updated.`); setOpen(false); setStep(1); setValue(""); setOtp(""); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full" data-testid={`change-${field}`}>Change</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Change {field}</DialogTitle></DialogHeader>
        {step === 1 ? (
          <div className="space-y-3">
            <Label>New {field}</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={field === "email" ? "new@email.com" : "+9198…"} data-testid={`new-${field}`} />
            <p className="text-xs text-muted-foreground">Your current {field} stays active until the new one is verified.</p>
            <DialogFooter><Button onClick={request} className="w-full rounded-full">Send code</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <Label>Verification code</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value)} className="font-mono tracking-widest" data-testid={`verify-${field}-otp`} />
            <DialogFooter><Button onClick={verify} className="w-full rounded-full">Verify & save</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ old_password: "", new_password: "" });
  const submit = async () => {
    try { await api.post("/auth/change-password", f); toast.success("Password changed."); setOpen(false); setF({ old_password: "", new_password: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full" data-testid="change-password">Change</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current password</Label><Input type="password" value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} className="mt-1.5" data-testid="old-pw" /></div>
          <div><Label>New password</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="mt-1.5" data-testid="new-pw" /></div>
          <DialogFooter><Button onClick={submit} className="w-full rounded-full">Update password</Button></DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
